/**
 * Frame preview engine — shared logic
 * -----------------------------------
 * Pure, dependency-light helpers shared by the product configurator, the
 * frame upsell modal and the collection preview overlays.
 *
 * Exposes `window.YarisFramePreview` with:
 *   - resolveFrameToken(colorName)  → 'black' | 'white' | 'natural' | ...
 *   - buildPreview({ imgSrc, alt, frameToken, mat, ratioPercent })  → HTMLElement
 *   - updatePreview(el, { imgSrc, alt, frameToken, mat, ratioPercent })
 *   - SIZE_LADDER / nextSizeUp(value)  → passepartout size mapping helpers
 *   - sizeValuesMatch(a, b)           → tolerant size comparison
 */
(function () {
  if (window.YarisFramePreview) return;

  /* Ordered list of the sizes we sell, smallest → largest. A passepartout is
   * always one rung larger than the poster it frames (outer dimension). */
  const SIZE_LADDER = ['13x18', '21x30', '30x40', '40x50', '50x70', '60x80', '70x100'];

  /* Colour-name → frame token. Covers Turkish + English naming used across the
   * frame catalogue. Falls back to a neutral natural wood. */
  const COLOR_RULES = [
    { token: 'black', re: /siyah|black|antrasit|mat\s*siyah/i },
    { token: 'white', re: /beyaz|white|kar/i },
    { token: 'dark-oak', re: /koyu|dark|f[uü]me|f[uü]me?\s*me[sş]e|antik/i },
    { token: 'walnut', re: /ceviz|walnut|venge|kahve|wenge/i },
    { token: 'gray', re: /gri|gray|grey|g[uü]m[uü][sş]|silver/i },
    { token: 'brass', re: /pirin[cç]|brass|g[oö]ld|alt[iı]n|bronz|bronze/i },
    { token: 'natural', re: /do[gğ]al|natural|ah[sş]ap|me[sş]e|oak|[cç]am|pine|naturel|wood|a[cç][iı]k/i },
  ];

  function normalize(str) {
    return String(str == null ? '' : str).trim();
  }

  /* border-image only paints with a non-zero border-width, and we want a
   * uniform, size-proportional moulding thickness. So we track each preview's
   * rendered width in `--fp-cw` (px) and derive border-width from it in CSS.
   * A single shared ResizeObserver keeps this in sync on every surface
   * (collection cards, PDP overlay, upsell modal) regardless of who sets width. */
  let _widthRO = null;
  function widthObserver() {
    if (_widthRO || typeof ResizeObserver === 'undefined') return _widthRO;
    _widthRO = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect ? entry.contentRect.width : entry.target.clientWidth;
        if (w) entry.target.style.setProperty('--fp-cw', `${w}px`);
      }
    });
    return _widthRO;
  }
  function observeWidth(el) {
    const ro = widthObserver();
    if (ro && el) ro.observe(el);
  }
  function unobserveWidth(el) {
    if (_widthRO && el) _widthRO.unobserve(el);
  }

  function parseSize(value) {
    const units = window.YarisSizeUnits;
    if (units && typeof units.parseSizeValue === 'function') {
      const parsed = units.parseSizeValue(value);
      if (parsed) return parsed;
    }
    const m = /(\d+(?:[.,]\d+)?)\s*[x×X]\s*(\d+(?:[.,]\d+)?)/.exec(String(value || ''));
    if (!m) return null;
    return {
      type: 'dim',
      w: parseFloat(m[1].replace(',', '.')) * 10,
      h: parseFloat(m[2].replace(',', '.')) * 10,
      original: value,
    };
  }

  function sizeValuesMatch(a, b) {
    if (!a || !b) return false;
    const na = normalize(a).toLowerCase();
    const nb = normalize(b).toLowerCase();
    if (na === nb) return true;
    const pa = parseSize(a);
    const pb = parseSize(b);
    if (pa && pb && pa.type === 'dim' && pb.type === 'dim') {
      // orientation-agnostic within 1mm
      const same = Math.abs(pa.w - pb.w) < 1 && Math.abs(pa.h - pb.h) < 1;
      const flipped = Math.abs(pa.w - pb.h) < 1 && Math.abs(pa.h - pb.w) < 1;
      return same || flipped;
    }
    if (pa && pb && pa.type === 'a' && pb.type === 'a') {
      return pa.label === pb.label;
    }
    const strip = (s) =>
      String(s || '')
        .toLowerCase()
        .replace(/\s*(cm|in|inch|in[cç]|")\s*/gi, '')
        .replace(/[×x]/g, 'x')
        .replace(/\s+/g, '');
    return strip(a) === strip(b);
  }

  /** Index of a size value inside the ladder (tolerant match). */
  function ladderIndex(value) {
    for (let i = 0; i < SIZE_LADDER.length; i += 1) {
      if (sizeValuesMatch(value, SIZE_LADDER[i])) return i;
    }
    return -1;
  }

  /* Explicit poster → passepartout (outer) size mapping. Business rule:
   * some poster sizes get no mat (largest size, and the odd 15x21), and 13x18
   * jumps straight to 21x30. Whatever mat size is chosen, the frame must match
   * that same size. */
  const MAT_SIZE_MAP = [
    { poster: '13x18', mat: '21x30' },
    { poster: '15x21', mat: null },
    { poster: '21x30', mat: '30x40' },
    { poster: '30x40', mat: '40x50' },
    { poster: '40x50', mat: '50x70' },
    { poster: '50x70', mat: null },
  ];

  /** The outer size a passepartout should have for the given poster size. */
  function matSizeFor(value) {
    for (const row of MAT_SIZE_MAP) {
      if (sizeValuesMatch(value, row.poster)) return row.mat;
    }
    return null;
  }

  /** Backwards-compatible alias (used by the upsell modal). */
  function nextSizeUp(value) {
    return matSizeFor(value);
  }

  function resolveMatToken(colorName) {
    const name = normalize(colorName);
    if (/keten|linen/i.test(name)) return 'linen';
    if (/kahverengi|brown|walnut|ceviz/i.test(name)) return 'brown';
    if (/siyah|black|antrasit/i.test(name)) return 'black';
    if (/krem|cream|ekru|ivory|beyaz|white/i.test(name)) return 'white';
    return 'white';
  }

  /* Schema defaults — used when theme settings have not been injected. */
  const MAT_HEX = {
    brown: '#4e3f38',
    black: '#2e2e2e',
    white: '#f4f0e8',
    linen: '#e4d7c3',
  };

  function matColorHex(token) {
    const colors = (window.YarisFrameAssets && window.YarisFrameAssets.matColors) || {};
    const fromSettings = colors[token];
    if (fromSettings && String(fromSettings).trim()) return String(fromSettings).trim();
    return MAT_HEX[token] || MAT_HEX.white;
  }

  function resolveFrameToken(colorName) {
    const name = normalize(colorName);
    if (!name) return 'natural';
    for (const rule of COLOR_RULES) {
      if (rule.re.test(name)) return rule.token;
    }
    return 'natural';
  }

  const KNOWN_TOKENS = ['none', 'black', 'white', 'natural', 'dark-oak', 'walnut', 'gray', 'brass'];

  /* Real (uploaded) frame & passepartout PNG models — rendered with
   * `border-image` (9-slice) so corners stay crisp and only the thin edges
   * stretch. This restores the photoreal look WITHOUT the earlier
   * `object-fit: fill` distortion that broke non-portrait ratios.
   *
   * URLs are injected by the theme via `window.YarisFrameAssets` (theme.liquid)
   * because plain JS can't resolve Shopify asset_url. The cropped assets sit
   * edge-to-edge (opaque bbox), so the border-image slices are exact.
   * Tokens without a PNG (gray/brass) gracefully fall back to the CSS moulding. */
  function frameAssets() {
    return (window.YarisFrameAssets && window.YarisFrameAssets.frames) || {};
  }

  /* Per-product passepartout geometry. Openings are fractions of the mat face.
   * Colour is not a layout: white/black/brown of the same outer+opening share
   * numbers today, but each family is its own entry so they can diverge. */
  function sizePairs(text) {
    const out = [];
    const re = /(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)/gi;
    let match;
    const source = String(text || '');
    while ((match = re.exec(source))) {
      out.push([parseFloat(match[1].replace(',', '.')), parseFloat(match[2].replace(',', '.'))]);
    }
    return out;
  }

  function hasPair(pairs, a, b) {
    return pairs.some(([w, h]) => (Math.abs(w - a) < 0.2 && Math.abs(h - b) < 0.2) || (Math.abs(w - b) < 0.2 && Math.abs(h - a) < 0.2));
  }

  function openingBox(outerW, outerH, openW, openH, place) {
    const w = openW / outerW;
    const h = openH / outerH;
    if (place === 'bottom-right') {
      const margin = 0.075;
      return {
        x: Math.max(0, 1 - margin - w),
        y: Math.max(0, 1 - margin - h),
        w,
        h,
      };
    }
    return { x: (1 - w) / 2, y: (1 - h) / 2, w, h };
  }

  function singleLayout(outerW, outerH, openW, openH, place, linen) {
    return {
      kind: 'single',
      linen: Boolean(linen),
      outer: [outerW, outerH],
      openingCm: [openW, openH],
      openings: [openingBox(outerW, outerH, openW, openH, place)],
    };
  }

  function resolveMatLayout(label) {
    const text = String(label || '');
    const pairs = sizePairs(text);
    if (!pairs.length && !/çift|cift|double|iki\s*pencere/i.test(text)) return null;
    const linen = /keten|linen/i.test(text);
    const doubleWin = /çift|cift|double|iki\s*pencere|2\s*pencere/i.test(text);
    if (doubleWin) {
      const outer = hasPair(pairs, 30, 40) ? [30, 40] : [50, 70];
      return {
        kind: 'double',
        linen,
        outer,
        openings: [
          { x: 0.56, y: 0.52, w: 0.36, h: 0.15 },
          { x: 0.56, y: 0.71, w: 0.36, h: 0.15 },
        ],
      };
    }
    if (linen && hasPair(pairs, 21, 30) && hasPair(pairs, 13, 18)) return singleLayout(21, 30, 13, 18, 'center', true);
    if (linen && hasPair(pairs, 21, 30) && hasPair(pairs, 10, 15)) return singleLayout(21, 30, 10, 15, 'center', true);
    if (hasPair(pairs, 50, 70) && hasPair(pairs, 13, 18)) return singleLayout(50, 70, 13, 18, 'bottom-right', false);
    if (hasPair(pairs, 30, 40) && hasPair(pairs, 10, 15)) return singleLayout(30, 40, 10, 15, 'center', false);
    if (hasPair(pairs, 21, 30) && hasPair(pairs, 10, 15)) return singleLayout(21, 30, 10, 15, 'center', linen);
    return null;
  }

  function layoutFitsPoster(layout, posterValue) {
    if (!layout) return false;
    const poster = parseSize(posterValue);
    if (!poster || poster.type !== 'dim') return false;
    if (layout.kind === 'double') return true;
    if (!layout.openingCm) return false;
    const pw = poster.w / 10;
    const ph = poster.h / 10;
    const a = layout.openingCm[0];
    const b = layout.openingCm[1];
    return (Math.abs(pw - a) < 0.2 && Math.abs(ph - b) < 0.2) || (Math.abs(pw - b) < 0.2 && Math.abs(ph - a) < 0.2);
  }

  const LINEN_PHOTOS = {
    1015: 'https://yarisposter.com/cdn/shop/files/keten.png?v=1790254324&width=1500',
    1318: 'https://yarisposter.com/cdn/shop/files/sonjeten.png?v=1790259833&width=1500',
  };

  // Custom mats are authored as portrait boards. A landscape artwork uses the
  // same geometry, transposed, so the window stays in the same corner.
  function orientLayout(layout, ratio) {
    if (!layout || !layout.outer || !(Number(ratio) > 1)) return layout;
    if (!(layout.outer[1] > layout.outer[0])) return layout;
    return {
      ...layout,
      outer: [layout.outer[1], layout.outer[0]],
      openingCm: layout.openingCm ? [layout.openingCm[1], layout.openingCm[0]] : layout.openingCm,
      openings: (layout.openings || []).map((box) => ({
        x: box.y,
        y: box.x,
        w: box.h,
        h: box.w,
      })),
    };
  }

  function linenKey(layout) {
    if (!layout || !layout.linen || !layout.openingCm) return '';
    const a = Math.round(Math.min(layout.openingCm[0], layout.openingCm[1]));
    const b = Math.round(Math.max(layout.openingCm[0], layout.openingCm[1]));
    if (a === 10 && b === 15) return '1015';
    if (a === 13 && b === 18) return '1318';
    return '';
  }

  function applyMatLayout(el, layout) {
    const mat = el.querySelector('.frame-preview__mat');
    if (!mat) return;
    mat.querySelectorAll('.frame-preview__window--extra').forEach((node) => node.remove());
    if (!layout || !layout.openings || !layout.openings.length) {
      el.classList.remove('has-mat-layout');
      delete el.dataset.matOuter;
      el.style.removeProperty('--fp-mat-outer');
      el.style.removeProperty('--fp-outer-w');
      el.style.removeProperty('--fp-outer-h');
      el.style.removeProperty('--fp-open-x');
      el.style.removeProperty('--fp-open-y');
      el.style.removeProperty('--fp-open-w');
      el.style.removeProperty('--fp-open-h');
      const primary = mat.querySelector('.frame-preview__window');
      if (primary) {
        primary.style.left = '';
        primary.style.top = '';
        primary.style.width = '';
        primary.style.height = '';
      }
      return;
    }
    el.classList.add('has-mat-layout');
    el.dataset.matOuter = `${layout.outer[0]}/${layout.outer[1]}`;
    el.style.setProperty('--fp-mat-outer', `${layout.outer[0]} / ${layout.outer[1]}`);
    el.style.setProperty('--fp-outer-w', String(layout.outer[0]));
    el.style.setProperty('--fp-outer-h', String(layout.outer[1]));
    const first = layout.openings[0];
    el.style.setProperty('--fp-open-x', `${first.x * 100}%`);
    el.style.setProperty('--fp-open-y', `${first.y * 100}%`);
    el.style.setProperty('--fp-open-w', `${first.w * 100}%`);
    el.style.setProperty('--fp-open-h', `${first.h * 100}%`);
    const primary = mat.querySelector('.frame-preview__window:not(.frame-preview__window--extra)');
    const primaryImg = primary ? primary.querySelector('img') : null;
    for (let i = 1; i < layout.openings.length; i += 1) {
      const box = layout.openings[i];
      const extra = document.createElement('div');
      extra.className = 'frame-preview__window frame-preview__window--extra';
      extra.style.left = `${box.x * 100}%`;
      extra.style.top = `${box.y * 100}%`;
      extra.style.width = `${box.w * 100}%`;
      extra.style.height = `${box.h * 100}%`;
      if (primaryImg) {
        const img = document.createElement('img');
        img.alt = primaryImg.alt || '';
        img.decoding = 'async';
        if (primaryImg.getAttribute('src')) img.src = primaryImg.getAttribute('src');
        extra.appendChild(img);
      }
      mat.appendChild(extra);
    }
  }

  function buildPreview(opts) {
    const el = document.createElement('div');
    el.className = 'frame-preview';
    const frame = document.createElement('div');
    frame.className = 'frame-preview__frame';
    const mat = document.createElement('div');
    mat.className = 'frame-preview__mat';
    const win = document.createElement('div');
    win.className = 'frame-preview__window';
    const img = document.createElement('img');
    img.decoding = 'async';
    img.loading = 'eager';
    const matOverlay = document.createElement('div');
    matOverlay.className = 'frame-preview__mat-overlay';
    matOverlay.setAttribute('aria-hidden', 'true');
    const frameOverlay = document.createElement('div');
    frameOverlay.className = 'frame-preview__frame-overlay';
    frameOverlay.setAttribute('aria-hidden', 'true');
    win.appendChild(img);
    mat.appendChild(win);
    frame.appendChild(mat);
    frame.appendChild(matOverlay);
    frame.appendChild(frameOverlay);
    el.appendChild(frame);
    observeWidth(el);
    updatePreview(el, opts);
    return el;
  }

  function updatePreview(el, opts) {
    if (!el) return;
    const options = opts || {};
    const token = KNOWN_TOKENS.includes(options.frameToken) ? options.frameToken : 'natural';
    el.setAttribute('data-frame', token);
    const layout = orientLayout(
      options.matLayout || (options.matLabel ? resolveMatLayout(options.matLabel) : null),
      options.ratio
    );
    const matToken = layout && layout.linen ? 'linen' : options.matToken || 'white';
    el.setAttribute('data-mat', matToken);
    el.style.setProperty('--fp-mat-bg', matColorHex(matToken));
    // A mat normally requires a frame; `allowMatWithoutFrame` enables the
    // frameless passepartout look (bare poster + white mat band).
    const matted = Boolean(options.mat) && (token !== 'none' || options.allowMatWithoutFrame === true);
    el.classList.toggle('is-matted', matted);

    // Real frame PNG (border-image) when available for this token; otherwise the
    // CSS moulding underneath acts as a graceful fallback (gray/brass/none).
    const frameSrc = token !== 'none' ? frameAssets()[token] || '' : '';
    el.classList.toggle('has-frame-png', Boolean(frameSrc));
    if (frameSrc) {
      el.style.setProperty('--fp-frame-src', `url("${frameSrc}")`);
    } else {
      el.style.removeProperty('--fp-frame-src');
    }

    // Do not paint the white mat PNG. It was inset by the frame band (light
    // rim when frameless) and tinted with CSS filters (wrong brown).
    el.classList.remove('has-mat-png');
    el.style.removeProperty('--fp-mat-src');

    // `ratio` is a numeric aspect ratio (width / height) used to lock the poster
    // window to the artwork's true proportions so the frame/mat wrap it cleanly
    // (poster embedded inside the frame, not stretched to fill the box).
    if (options.ratio != null && options.ratio !== '' && Number.isFinite(Number(options.ratio))) {
      el.style.setProperty('--fp-ratio', String(options.ratio));
    } else if (options.ratioPercent != null && options.ratioPercent !== '') {
      el.style.setProperty('--fp-ratio', `${options.ratioPercent}%`);
    }

    applyMatLayout(el, matted ? layout : null);
    const linen = matted ? linenKey(layout) : '';
    if (linen && LINEN_PHOTOS[linen]) {
      el.dataset.linen = linen;
      el.style.setProperty('--fp-linen', `url("${LINEN_PHOTOS[linen]}")`);
    } else {
      delete el.dataset.linen;
      el.style.removeProperty('--fp-linen');
    }
    el.classList.toggle('is-landscape', Boolean(matted && layout && Number(options.ratio) > 1));

    const win = el.querySelector('.frame-preview__window');
    const img = el.querySelector('.frame-preview__window img');
    if (img) {
      if (options.imgSrc != null && img.getAttribute('src') !== options.imgSrc) {
        img.src = options.imgSrc;
      }
      if (options.srcset != null) img.srcset = options.srcset;
      if (options.alt != null) img.alt = options.alt;

      // `crop` ({ left, top, w, h } as fractions of the source image) shows only
      // the detected poster region, cropping away studio/gray margins that are
      // baked into the product photo. Without it, the image fills the window.
      const crop = options.crop;
      if (crop && crop.w > 0 && crop.h > 0) {
        if (win) win.style.overflow = 'hidden';
        img.style.position = 'absolute';
        img.style.width = `${100 / crop.w}%`;
        img.style.height = `${100 / crop.h}%`;
        img.style.left = `${(-crop.left / crop.w) * 100}%`;
        img.style.top = `${(-crop.top / crop.h) * 100}%`;
        img.style.maxWidth = 'none';
        img.style.objectFit = 'fill';
      } else {
        img.style.position = '';
        img.style.width = '';
        img.style.height = '';
        img.style.left = '';
        img.style.top = '';
        img.style.maxWidth = '';
        img.style.objectFit = '';
      }
      el.querySelectorAll('.frame-preview__window--extra img').forEach((extraImg) => {
        if (img && img.getAttribute('src')) extraImg.src = img.getAttribute('src');
        extraImg.alt = img ? img.alt || '' : '';
      });
    }
  }

  /* --------------------------------------------------------------------------
   * Smart poster-region detection
   * --------------------------------------------------------------------------
   * Product photos are often studio mockups: the poster sits centred on a
   * gray/white background (often with uneven lighting + a soft shadow). To frame
   * only the poster (regardless of portrait/landscape/square), we model the
   * background as a bilinear gradient from the four corners, then take the
   * row/column density of pixels that deviate from that local background.
   * Returns { left, top, w, h } as fractions of the image, or null when the
   * image is edge-to-edge art, uniform, or cross-origin tainted. */
  const _boundsCache = new Map();

  /* Reload the image cross-origin so the canvas isn't tainted (the DOM <img>
   * is usually loaded without CORS, which would block getImageData). Shopify's
   * CDN serves images with permissive CORS headers. */
  function loadCrossOrigin(src) {
    return new Promise((resolve, reject) => {
      const im = new Image();
      im.crossOrigin = 'anonymous';
      im.decoding = 'async';
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = src;
    });
  }

  function computeContentBounds(imgOrEl) {
    const src = imgOrEl && (imgOrEl.currentSrc || imgOrEl.src);
    if (!src) return Promise.resolve(null);
    return loadCrossOrigin(src)
      .then((img) => analyzeBounds(img))
      .catch(() => null);
  }

  function analyzeBounds(img) {
    return new Promise((resolve) => {
      try {
        const W = img.naturalWidth;
        const H = img.naturalHeight;
        if (!W || !H) return resolve(null);

        const scale = Math.min(1, 340 / Math.max(W, H));
        const cw = Math.max(8, Math.round(W * scale));
        const ch = Math.max(8, Math.round(H * scale));
        const canvas = document.createElement('canvas');
        canvas.width = cw;
        canvas.height = ch;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return resolve(null);
        ctx.drawImage(img, 0, 0, cw, ch);

        let data;
        try {
          data = ctx.getImageData(0, 0, cw, ch).data;
        } catch (e) {
          return resolve(null); // tainted canvas (CORS) → graceful fallback
        }

        const at = (x, y) => (y * cw + x) * 4;

        // Model the studio background as a bilinear gradient between the four
        // corner colours. This cancels uneven studio lighting (one side darker)
        // so a poster's near-white paper border is still distinguished from the
        // gray backdrop, and shadows/gradients don't skew the detected box.
        const corner = (x0, y0) => {
          let r = 0;
          let g = 0;
          let b = 0;
          let c = 0;
          for (let dx = 0; dx < 6; dx += 1) {
            for (let dy = 0; dy < 6; dy += 1) {
              const x = Math.min(cw - 1, x0 + dx);
              const y = Math.min(ch - 1, y0 + dy);
              const i = at(x, y);
              r += data[i];
              g += data[i + 1];
              b += data[i + 2];
              c += 1;
            }
          }
          return [r / c, g / c, b / c];
        };
        const tl = corner(0, 0);
        const tr = corner(cw - 6, 0);
        const bl = corner(0, ch - 6);
        const brc = corner(cw - 6, ch - 6);
        const denomX = cw - 1 || 1;
        const denomY = ch - 1 || 1;
        const bgAt = (x, y) => {
          const fx = x / denomX;
          const fy = y / denomY;
          const out = [0, 0, 0];
          for (let k = 0; k < 3; k += 1) {
            const top = tl[k] * (1 - fx) + tr[k] * fx;
            const bot = bl[k] * (1 - fx) + brc[k] * fx;
            out[k] = top * (1 - fy) + bot * fy;
          }
          return out;
        };

        const devAt = (x, y) => {
          const i = at(x, y);
          const b = bgAt(x, y);
          return Math.abs(data[i] - b[0]) + Math.abs(data[i + 1] - b[1]) + Math.abs(data[i + 2] - b[2]);
        };

        // Residual background noise on the border ring → adaptive threshold.
        let ring = 0;
        let rc = 0;
        for (let x = 0; x < cw; x += 1) {
          ring += devAt(x, 0) + devAt(x, ch - 1);
          rc += 2;
        }
        for (let y = 0; y < ch; y += 1) {
          ring += devAt(0, y) + devAt(cw - 1, y);
          rc += 2;
        }
        ring /= rc || 1;
        const thr = Math.max(14, ring * 2 + 8);

        // Column/row density of poster pixels. A column through the poster has a
        // high fraction of deviating pixels (paper fills most of its height),
        // while background columns stay near zero.
        const colD = new Array(cw).fill(0);
        const rowD = new Array(ch).fill(0);
        for (let y = 0; y < ch; y += 1) {
          for (let x = 0; x < cw; x += 1) {
            if (devAt(x, y) > thr) {
              colD[x] += 1;
              rowD[y] += 1;
            }
          }
        }
        const colThresh = ch * 0.3;
        const rowThresh = cw * 0.3;
        let minX = -1;
        let maxX = -1;
        let minY = -1;
        let maxY = -1;
        for (let x = 0; x < cw; x += 1) {
          if (colD[x] >= colThresh) {
            if (minX < 0) minX = x;
            maxX = x;
          }
        }
        for (let y = 0; y < ch; y += 1) {
          if (rowD[y] >= rowThresh) {
            if (minY < 0) minY = y;
            maxY = y;
          }
        }
        if (minX < 0 || minY < 0 || maxX <= minX || maxY <= minY) return resolve(null);

        let left = minX / cw;
        let top = minY / ch;
        let w = (maxX + 1 - minX) / cw;
        let h = (maxY + 1 - minY) / ch;
        // Edge-to-edge art → treat as no crop (frame the whole image).
        if (w > 0.95 && h > 0.95) return resolve(null);

        // Small symmetric safety inset. The detected edge lands in the soft
        // gray↔paper transition, so nudge inward onto the poster's blank paper
        // border — this removes the residual gray sliver without any visible
        // loss (measured: the trimmed edge is the cream paper margin).
        const pad = 0.018;
        left += pad;
        top += pad;
        w -= 2 * pad;
        h -= 2 * pad;
        if (w <= 0.1 || h <= 0.1) return resolve(null);

        resolve({ left, top, w, h });
      } catch (e) {
        resolve(null);
      }
    });
  }

  /** Cached wrapper — resolves quickly for repeat lookups of the same src. */
  function getContentBounds(img) {
    const key = img && (img.currentSrc || img.src);
    if (!key) return Promise.resolve(null);
    if (_boundsCache.has(key)) return Promise.resolve(_boundsCache.get(key));
    return computeContentBounds(img).then((bounds) => {
      _boundsCache.set(key, bounds);
      return bounds;
    });
  }

  function cachedContentBounds(img) {
    const key = img && (img.currentSrc || img.src);
    return key && _boundsCache.has(key) ? _boundsCache.get(key) : undefined;
  }

  /* --------------------------------------------------------------------------
   * Layout helpers (shared by every surface so orientation handling is
   * identical for portrait / landscape / square, with or without a crop).
   * ------------------------------------------------------------------------ */

  /** Poster aspect (w/h) from the image's natural size, narrowed by the crop. */
  function imageRatio(img, crop) {
    const nw = img && img.naturalWidth > 0 ? img.naturalWidth : null;
    const nh = img && img.naturalHeight > 0 ? img.naturalHeight : null;
    let a;
    if (nw && nh) {
      a = nw / nh;
    } else {
      const r = img && img.getBoundingClientRect ? img.getBoundingClientRect() : null;
      a = r && r.width && r.height ? r.width / r.height : 1;
    }
    if (crop && crop.w > 0 && crop.h > 0) a *= crop.w / crop.h;
    return a;
  }

  /* Frame moulding / mat band thickness as a fraction of composite width. Must
   * match the CSS (--fp-frame-w 2.1%, .is-matted --fp-mat-w 11%). */
  function frameMatFractions(token, matted) {
    return {
      f: token === 'none' ? 0 : 0.021,
      m: matted ? 0.11 : 0,
    };
  }

  /** Outer aspect (w/h) of the whole composite (frame + mat + poster). */
  function compositeAspect(ratio, token, matted) {
    const { f, m } = frameMatFractions(token, matted);
    const a = ratio || 1;
    const k = (1 - 2 * (f + m)) / a + 2 * (f + m);
    return k > 0 ? 1 / k : a;
  }

  window.YarisFramePreview = {
    SIZE_LADDER,
    normalize,
    parseSize,
    sizeValuesMatch,
    ladderIndex,
    matSizeFor,
    nextSizeUp,
    resolveFrameToken,
    resolveMatToken,
    resolveMatLayout,
    layoutFitsPoster,
    orientLayout,
    linenKey,
    matColorHex,
    buildPreview,
    updatePreview,
    unobserveWidth,
    computeContentBounds,
    getContentBounds,
    cachedContentBounds,
    imageRatio,
    compositeAspect,
  };
})();
